import {
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  ListItemText,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Theme,
} from "@mui/material";
import { useMemo, useState } from "react";
import NewPageLabelDialog from "./NewPageLabelDialog";
import { Link, Outlet, useParams } from "react-router-dom";
import { makePageLabelForCopy, usePageLabelsLoader } from "loaders/page-labels.loader";
import { PageLabelListing, PageLabelListingType } from "generated/graphql";
import { P } from "theme/typography";
import CopyToClipboard from "components/CopyToClipboard";
import { ClipboardPageLabel } from "routes/lens-editor/page-labels/ClipboardPageLabel";
import { EllipsisSX, HoverLinkSX } from "theme";
import { parseSafeInt } from "Backend";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { fetchPageLabelStats, selectPageLabelStats, selectPageLabelStatsStatus } from "redux/slices/lensEditor";
import { empty } from "helpers/coll";
import truncateString from "helpers/truncateString";
import Stats from "@mui/icons-material/QueryStats";
import RequestStatus from "redux/slices/RequestStatus";
import LoadingSpinner from "components/LoadingSpinner";

const NavSX: SxProps<Theme> = {
  ...HoverLinkSX,
  color: "grey.900",
};

const PageLabels = () => {
  const params = useParams();
  const lensId = parseSafeInt(params.lensId);
  const dispatch = useAppDispatch();
  const pageLabels = usePageLabelsLoader();
  const pageLabelStats = useAppSelector(selectPageLabelStats);
  const pageLabelStatsStatus = useAppSelector(selectPageLabelStatsStatus);
  const [search, setSearch] = useState("");
  const [showNewPageLabel, setShowNewPageLabel] = useState<boolean>(false);
  const [checked, setChecked] = useState<PageLabelListing[]>([]);
  const pageLabelsForCopy: ClipboardPageLabel[] = useMemo(
    () => checked.map((c) => makePageLabelForCopy(c.label, c.rules)),
    [checked]
  );
  const filteredPageLabels = useMemo(
    () =>
      pageLabels.filter(
        (pl) =>
          pl.label.name.toLowerCase().indexOf(search.toLowerCase()) !== -1 &&
          pl.pageLabelType === PageLabelListingType.RuleBased
      ),
    [search, pageLabels]
  );

  if (lensId === undefined) {
    throw new Error("Invalid or missing lens id");
  }

  const handleToggle = (value: PageLabelListing) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const reloadStats = async () => {
    await dispatch(fetchPageLabelStats({ lensId })).unwrap();
  };

  const loadingStats = pageLabelStatsStatus === RequestStatus.PENDING;

  return (
    <Box sx={{ display: "grid", gap: "16px 16px", gridTemplateColumns: "1fr 2fr", height: "100%", p: 1 }}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl fullWidth>
            <TextField label="Search Page Labels" value={search} onChange={(evt) => setSearch(evt.target.value)} />
          </FormControl>
          <Button size="small" color="primary" variant="contained" onClick={() => setShowNewPageLabel(true)}>
            New
          </Button>
          {showNewPageLabel && (
            <NewPageLabelDialog open={showNewPageLabel} close={() => setShowNewPageLabel(false)} lensId={lensId} />
          )}
          <Tooltip title={empty(pageLabelStats) ? "Load Stats" : "Reload Stats"} placement="top">
            <IconButton size="small" onClick={reloadStats} disabled={loadingStats}>
              {loadingStats ? <LoadingSpinner size={20} /> : <Stats sx={{ width: "20px" }} />}
            </IconButton>
          </Tooltip>
          <CopyToClipboard
            disabled={empty(pageLabelsForCopy)}
            text={JSON.stringify(pageLabelsForCopy, null, 2)}
            title="Copy Selected Page Labels for Import"
          />
        </Box>
        <TableContainer>
          <Table size="small" sx={{ mt: 1, tableLayout: "fixed" }} stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "180px" }}>Name</TableCell>
                <TableCell>Rules</TableCell>
                <TableCell>Hits</TableCell>
                <TableCell>Cases</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPageLabels.map((pageLabelListing) => {
                const { label, rules } = pageLabelListing;
                const selected = params.pageLabelId === label.id.toString();
                const stats = pageLabelStats.find((pls) => pls.labelId === label.id);
                return (
                  <TableRow key={label.id} selected={selected}>
                    <TableCell sx={EllipsisSX} title={label.name}>
                      <Link to={label.id.toString()}>
                        <P sx={NavSX}>
                          <ListItemText primary={truncateString(label.name, 30)} />
                        </P>
                      </Link>
                    </TableCell>
                    <TableCell>{rules.length}</TableCell>
                    <TableCell>{stats?.pageCount}</TableCell>
                    <TableCell>{stats?.caseCount}</TableCell>
                    <TableCell>
                      <Checkbox
                        edge="end"
                        onChange={handleToggle(pageLabelListing)}
                        checked={checked.indexOf(pageLabelListing) !== -1}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ minHeight: 0 }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default PageLabels;
